import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  CSAT_BODY_BY_EXPERIENCE,
  CSAT_HEADER,
  CSAT_SUBMIT_SUCCESS_MESSAGE,
  CSAT_SCORE_LABELS,
  CSAT_EXPERIENCE_APPLICATION,
  type CsatExperienceType,
} from '@/features/csat/constants/csatConstants'
import { CsatScoreFace } from '@/components/ui/CsatScoreFace'
import { dismissCsatResponse, submitCsatResponse } from '@/features/csat/services/csatService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'

const SCORES: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5]

function buildCsatWritePayload(
  experience: CsatExperienceType,
  fkApplicantId?: string | number,
  fkReimbursementId?: string | number,
) {
  return {
    experience,
    ...(experience === CSAT_EXPERIENCE_APPLICATION && fkApplicantId != null
      ? { fk_applicant_id: fkApplicantId }
      : {}),
    ...(fkReimbursementId != null ? { fk_reimbursement_id: fkReimbursementId } : {}),
  }
}

export interface CsatSurveyModalProps {
  isOpen: boolean
  experience: CsatExperienceType
  fkApplicantId?: string | number
  fkReimbursementId?: string | number
  onClose: () => void
}

export function CsatSurveyModal({
  isOpen,
  experience,
  fkApplicantId,
  fkReimbursementId,
  onClose,
}: CsatSurveyModalProps) {
  const [selectedScore, setSelectedScore] = useState<1 | 2 | 3 | 4 | 5 | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const persistedRef = useRef(false)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) {
      setSelectedScore(null)
      setSubmitting(false)
      persistedRef.current = false
    }
  }, [isOpen])

  const handleDismiss = useCallback(async () => {
    if (submitting) return
    if (persistedRef.current) {
      onClose()
      return
    }
    setSubmitting(true)
    try {
      await dismissCsatResponse(
        buildCsatWritePayload(experience, fkApplicantId, fkReimbursementId),
      )
      persistedRef.current = true
      onClose()
    } catch (err) {
      showApiError(err)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }, [experience, fkApplicantId, fkReimbursementId, onClose, submitting])

  const handleSubmit = useCallback(async () => {
    if (selectedScore === null || submitting) return
    setSubmitting(true)
    try {
      await submitCsatResponse({
        ...buildCsatWritePayload(experience, fkApplicantId, fkReimbursementId),
        score: selectedScore,
      })
      persistedRef.current = true
      showSuccess(CSAT_SUBMIT_SUCCESS_MESSAGE)
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setSubmitting(false)
    }
  }, [experience, fkApplicantId, fkReimbursementId, onClose, selectedScore, submitting])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) {
        void handleDismiss()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleDismiss, isOpen, submitting])

  if (!isOpen) return null

  const bodyCopy = CSAT_BODY_BY_EXPERIENCE[experience]

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid="csat-survey-modal"
      onClick={() => {
        if (!submitting) void handleDismiss()
      }}
    >
      <div
        className="w-full max-w-[710px] animate-slideUp overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="csat-survey-modal-title"
        aria-describedby="csat-survey-modal-description"
      >
        <div className="flex items-center justify-between border-b border-[#e2e8f0] bg-[#f0f4ff] px-6 py-4">
          <h2 id="csat-survey-modal-title" className="text-xl font-bold text-[#173f7a]">
            {CSAT_HEADER}
          </h2>
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={submitting}
            className="text-slate-800 transition-colors hover:text-gray-900 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 px-6 pb-6 pt-5" id="csat-survey-modal-description">
          <p className="text-left text-base font-semibold leading-snug text-[#233e61]">{bodyCopy}</p>

          <div
            className="flex w-full gap-2"
            role="radiogroup"
            aria-label={bodyCopy}
          >
            {SCORES.map((score) => {
              const checked = selectedScore === score
              return (
                <label
                  key={score}
                  className={`flex min-h-[112px] flex-1 cursor-pointer flex-col items-center justify-start gap-2.5 rounded-lg border bg-white px-[7px] py-[15px] text-center transition-colors ${
                    checked
                      ? 'border-[#1686ff] bg-[#edf6ff]'
                      : 'border-[#d9e4f0]'
                  }`}
                >
                  <span
                    className="flex h-12 w-12 shrink-0 items-center justify-center"
                    role="img"
                    aria-label={CSAT_SCORE_LABELS[score]}
                  >
                    <CsatScoreFace score={score} />
                  </span>
                  <input
                    type="radio"
                    name="csat-score"
                    value={score}
                    checked={checked}
                    onChange={() => setSelectedScore(score)}
                    className="h-[17px] w-[17px] accent-[#1686ff]"
                  />
                  <span className="text-sm font-medium leading-snug text-[#233e61]">
                    {CSAT_SCORE_LABELS[score]}
                  </span>
                </label>
              )
            })}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={submitting}
            className="w-full rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-[#173f7a] transition-colors hover:bg-gray-50 sm:w-auto sm:min-w-[140px]"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={selectedScore === null || submitting}
            className="w-full rounded-lg bg-[#1597ff] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[180px]"
          >
            Submit Feedback
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
